import cx from 'classnames';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useInfiniteHits, useInstantSearch } from 'react-instantsearch';

import { Container, ScrollArrows } from 'components/@tedui';

import { useTrackClickEvent } from '../analytics';
import { CustomSearchBox } from './CustomSearchBox';
import { Duration, FILTER_BAR_CONSTANTS, SortBy, Subtitles } from './Dropdowns';
import { TopicsButton } from './TopicsButton';

export function FilterBar(): React.ReactNode {
  const router = useRouter();
  const ISTALKSPAGE = router.pathname === '/talks';

  const { status } = useInstantSearch();
  const { results } = useInfiniteHits();
  const [isFilterBarHidden, setIsFilterBarHidden] = useState(true);
  const btnSortBy = useRef(null);
  const btnDuration = useRef(null);
  const [showArrows, setShowArrows] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [isPageScrolled, setIsPageScrolled] = useState(false);

  const trackClick = useTrackClickEvent();

  const classNames = {
    container: cx(
      'sticky top-0 z-40 bg-gray-50 pb-6 pt-4 transition-colors duration-200',
      {
        hidden: !ISTALKSPAGE,
        'mb-4 lg-tui:mb-0': !isFilterBarHidden || isPageScrolled,
        'pointer-events-none opacity-50': status === 'error'
      }
    ),
    filterBar: 'flex flex-col lg-tui:flex-row',
    searchWrapper: '-mb-2 w-full lg-tui:mb-0 lg-tui:max-w-[220px]',
    scrollArea: cx(
      'lg-tui:ml-2 lg-tui:flex lg-tui:grow lg-tui:justify-between',
      isFilterBarHidden
        ? 'hidden'
        : 'mt-4 flex overflow-auto scroll-smooth pt-2 scrollbar-hide lg-tui:mt-0 lg-tui:overflow-visible lg-tui:pt-0'
    ),
    dropdownsWrapper: 'flex justify-end',
    dropdowns: 'flex justify-end gap-2 md-tui:gap-4',
    sortDropdownWrapper: cx(
      { 'pr-2': !isFilterBarHidden },
      'w-[106px] min-w-max flex-shrink-0 lg-tui:pr-2'
    ),
    topicsButtonWrapper: 'w-[109px]',
    subtitlesDropdownWrapper: 'w-[120px]',
    durationDropdownWrapper: 'w-[120px]'
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollThreshold = 10;

      if (window.scrollY > scrollThreshold && !isPageScrolled) {
        setIsPageScrolled(true);
      } else if (window.scrollY <= scrollThreshold && isPageScrolled) {
        setIsPageScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isPageScrolled]);

  useEffect(() => {
    if (scrollAreaRef.current === null) return;
    const checkOverflow = () => {
      if (scrollAreaRef.current) {
        setShowArrows(
          scrollAreaRef.current.offsetWidth < scrollAreaRef.current.scrollWidth
        );
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [isFilterBarHidden]);

  const handleToggleFilterBar = () => {
    setIsFilterBarHidden(!isFilterBarHidden);

    if (isFilterBarHidden) {
      trackClick({
        eventName: 'show_filter_bar'
      });
    }

    if (!isFilterBarHidden) {
      trackClick({
        eventName: 'dismiss_filter_bar'
      });
    }
  };

  return (
    <Container
      className={classNames.container}
      id={`FilterBar-${ISTALKSPAGE ? 'Talks' : 'Home'}`}
    >
      <Container
        className={classNames.filterBar}
        testId="filterBar"
        id="filterBar"
      >
        <div className={classNames.searchWrapper}>
          <CustomSearchBox filterOnClick={handleToggleFilterBar} />
        </div>

        <div
          className={classNames.scrollArea}
          id="filterBarScrollArea"
          ref={scrollAreaRef}
        >
          <ScrollArrows
            scrollAreaRef={scrollAreaRef}
            showArrows={showArrows}
            {...(!ISTALKSPAGE && {
              gradientColor: 'white'
            })}
          />

          <div ref={btnSortBy} className={classNames.sortDropdownWrapper}>
            <SortBy
              {...{
                showDefaultSort:
                  (!!results?.query && results?.query.length > 0) ||
                  (!results?.query &&
                    FILTER_BAR_CONSTANTS.sortByOptions.some(
                      option => option.value === results?.index
                    ))
              }}
            />
          </div>

          <div className={classNames.dropdownsWrapper}>
            <div className={classNames.dropdowns}>
              <div className={classNames.topicsButtonWrapper}>
                <TopicsButton />
              </div>
              <div className={classNames.subtitlesDropdownWrapper}>
                <Subtitles />
              </div>
              <div
                ref={btnDuration}
                className={classNames.durationDropdownWrapper}
              >
                <Duration />
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Container>
  );
}
